/**
 * Google Tag Manager (GTM) Utility Service
 * This service provides functions to track events and user interactions
 * throughout the L&T Finance poster creation application
 */

class GTMService {
  constructor() {
    this.isGTMLoaded = false;
    this.checkGTMStatus();
  }

  /**
   * Check if GTM is properly loaded
   */
  checkGTMStatus() {
    const checkInterval = setInterval(() => {
      if (window.dataLayer && window.google_tag_manager) {
        this.isGTMLoaded = true;
        console.log('✅ GTM Service: GTM is loaded and ready');
        clearInterval(checkInterval);
      }
    }, 100);

    // Stop checking after 10 seconds
    setTimeout(() => {
      if (!this.isGTMLoaded) {
        console.warn('⚠️ GTM Service: GTM not detected after 10 seconds');
      }
      clearInterval(checkInterval);
    }, 10000);
  }

  /**
   * Push an event to dataLayer
   * @param {Object} eventData - The event data to push
   */
  pushEvent(eventData) {
    if (window.dataLayer) {
      window.dataLayer.push(eventData);
      console.log('📤 GTM Event Pushed:', eventData);
    } else {
      console.warn('⚠️ GTM Service: dataLayer not available, event not sent:', eventData);
    }
  }

  /**
   * Track page views
   * @param {string} pageName - Name of the page
   * @param {string} pageTitle - Title of the page
   * @param {string} pagePath - Path of the page
   */
  trackPageView(pageName, pageTitle, pagePath) {
    this.pushEvent({
      event: 'ltf_page_view',
      page_name: pageName,
      page_title: pageTitle,
      page_path: pagePath,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track OTP verification events
   * @param {string} action - 'request', 'verify_success', 'verify_failed'
   * @param {string} phoneNumber - User's phone number (masked for privacy)
   * @param {Object} additionalData - Any additional data
   */
  trackOTPEvent(action, phoneNumber, additionalData = {}) {
    // Mask phone number for privacy
    const maskedPhone = phoneNumber ? phoneNumber.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2') : 'unknown';
    
    this.pushEvent({
      event: 'ltf_otp_event',
      event_category: 'authentication',
      event_action: action,
      phone_number_masked: maskedPhone,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }

  /**
   * Track form submissions and completions
   * @param {string} formType - Type of form ('user_details', 'company_info', etc.)
   * @param {Object} formData - Form data (sanitized for privacy)
   */
  trackFormSubmission(formType, formData = {}) {
    // Sanitize form data - remove sensitive information
    const sanitizedData = {
      industry: formData.industry,
      keywords_count: formData.keywords ? formData.keywords.length : 0,
      has_company_data: !!formData.companyName
    };

    this.pushEvent({
      event: 'ltf_form_submission',
      event_category: 'form_interaction',
      event_action: 'submit',
      form_type: formType,
      form_data: sanitizedData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track photo upload events
   * @param {string} action - 'upload_start', 'upload_success', 'upload_failed', 'crop_complete'
   * @param {Object} photoData - Photo metadata (size, type, etc.)
   */
  trackPhotoEvent(action, photoData = {}) {
    this.pushEvent({
      event: 'ltf_photo_event',
      event_category: 'photo_interaction',
      event_action: action,
      photo_type: photoData.type || 'unknown',
      photo_size_kb: photoData.size ? Math.round(photoData.size / 1024) : null,
      is_selfie_mode: photoData.isSelfieMode || false,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track poster generation and download events
   * @param {string} action - 'generation_start', 'generation_success', 'generation_failed', 'download', 'share'
   * @param {Object} posterData - Poster metadata
   */
  trackPosterEvent(action, posterData = {}) {
    this.pushEvent({
      event: 'ltf_poster_event',
      event_category: 'poster_interaction',
      event_action: action,
      industry: posterData.industry,
      template_used: posterData.templatePath,
      generation_time_ms: posterData.generationTime,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user journey progression
   * @param {string} stage - Current stage in the user journey
   * @param {Object} stageData - Data about the current stage
   */
  trackUserJourney(stage, stageData = {}) {
    this.pushEvent({
      event: 'ltf_user_journey',
      event_category: 'user_flow',
      event_action: 'stage_reached',
      journey_stage: stage,
      stage_data: stageData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track business engagement (CTA clicks, external links)
   * @param {string} action - 'cta_click', 'external_link', 'apply_now'
   * @param {string} linkType - Type of engagement
   * @param {string} destination - Where the link leads
   */
  trackBusinessEngagement(action, linkType, destination) {
    this.pushEvent({
      event: 'ltf_business_engagement',
      event_category: 'business_conversion',
      event_action: action,
      link_type: linkType,
      destination: destination,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track errors and technical issues
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   * @param {string} location - Where the error occurred
   */
  trackError(errorType, errorMessage, location) {
    this.pushEvent({
      event: 'ltf_error',
      event_category: 'technical_error',
      event_action: errorType,
      error_message: errorMessage,
      error_location: location,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user engagement metrics
   * @param {string} action - 'time_on_page', 'scroll_depth', 'interaction'
   * @param {Object} engagementData - Engagement metrics
   */
  trackEngagement(action, engagementData = {}) {
    this.pushEvent({
      event: 'ltf_engagement',
      event_category: 'user_engagement',
      event_action: action,
      engagement_data: engagementData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track conversion events
   * @param {string} conversionType - Type of conversion
   * @param {Object} conversionData - Conversion data
   */
  trackConversion(conversionType, conversionData = {}) {
    this.pushEvent({
      event: 'ltf_conversion',
      event_category: 'conversion',
      event_action: conversionType,
      conversion_data: conversionData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Enhanced debugging method
   */
  debug() {
    console.log('🔍 GTM Service Debug Information:');
    console.log('GTM Loaded:', this.isGTMLoaded);
    console.log('DataLayer exists:', !!window.dataLayer);
    console.log('DataLayer length:', window.dataLayer ? window.dataLayer.length : 'N/A');
    console.log('GTM Container exists:', !!window.google_tag_manager);
    
    if (window.google_tag_manager) {
      console.log('GTM Containers:', Object.keys(window.google_tag_manager));
    }

    // Show recent dataLayer entries
    if (window.dataLayer && window.dataLayer.length > 0) {
      console.log('Recent DataLayer entries (last 5):');
      const recent = window.dataLayer.slice(-5);
      recent.forEach((entry, index) => {
        console.log(`${index + 1}:`, entry);
      });
    }
  }
}

// Create singleton instance
const gtmService = new GTMService();

export default gtmService;
